import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "../Header";
import Footer from "../Footer";
import config from "../common/Config";
import { Box, Grid, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Skeleton, Alert, TextField, Tooltip, IconButton, InputAdornment, Grid2 } from "@mui/material";
import { Iconify } from "src/index";
import "../common/commonStyles.css";
import { encryptPayload, resolveEncryptedResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const infoColumns = ["code", "name", "categories", "brand", "size"];
const boxColumns = ["box_cost", "price", "box_diff", "quantity"];
const pieceColumns = ["piece_cost", "piece_price", "piece_diff", "split_quantity"];
const columns = [...infoColumns, ...boxColumns, ...pieceColumns];
const productDetailWidths = {
  code: "7%",
  name: "20%",
  categories: "12%",
  brand: "8%",
  size: "7%"
};
const numericColumnWidth = "60px";
const quantityColumnWidth = "40px";
const columnWidths = [
  productDetailWidths.code,
  productDetailWidths.name,
  productDetailWidths.categories,
  productDetailWidths.brand,
  productDetailWidths.size,
  numericColumnWidth,
  numericColumnWidth,
  numericColumnWidth,
  quantityColumnWidth,
  numericColumnWidth,
  numericColumnWidth,
  numericColumnWidth,
  quantityColumnWidth
];

const borderColor = "rgba(99, 99, 97, 0.16)";
const headerBgColor = "#f5f7fa";
const numericCellBgColor = "#fffcfa";
const headerNumericCellBgColor = "#edf4fc";

const commonStyles = {
  headerCell: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#212b36",
    py: 1.5,
    borderRight: `1px solid ${borderColor}`,
    backgroundColor: headerBgColor
  },
  headerNumericCell: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#212b36",
    py: 1.5,
    borderRight: `1px solid ${borderColor}`,
    backgroundColor: headerNumericCellBgColor
  },
  headerColspanCell: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#212b36",
    py: 1.5,
    borderBottom: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
    backgroundColor: headerBgColor
  },
  bodyCell: {
    py: { xs: 1, sm: 1.5 },
    fontSize: { xs: "0.7rem", sm: "0.75rem" },
    color: "#212b36"
  },
  bodyNumericCell: {
    py: { xs: 1, sm: 1.5 },
    fontSize: { xs: "0.7rem", sm: "0.75rem" },
    color: "#212b36",
    backgroundColor: numericCellBgColor
  }
};

const Inventory_Report = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
    }
  }, [accessToken]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      setPage(0);
      setSearchTerm(trimmed);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const settings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(settings);
        const headers = {
          authorization: `Bearer ${token}`,
        };
        let body;
        if (useEncrypt) {
          const requestData = {
            draw: page + 1,
            start: page * rowsPerPage,
            length: rowsPerPage,
            searchValue: searchTerm,
            searchField: "code",
            categoryValue: 0,
          };
          const encryptedPayload = encryptPayload(requestData);
          if (!encryptedPayload) {
            if (active) {
              setRows([]);
              setTotalCount(0);
              setError("");
              setLoading(false);
            }
            return;
          }
          const formData = new FormData();
          formData.append("encrypted_payload", encryptedPayload);
          body = formData;
        } else {
          const params = new URLSearchParams();
          params.append("draw", String(page + 1));
          params.append("start", String(page * rowsPerPage));
          params.append("length", String(rowsPerPage));
          params.append("searchValue", searchTerm);
          params.append("searchField", "code");
          params.append("search[value]", searchTerm);
          params.append("search[regex]", "false");
          params.append("categoryValue", "0");
          columns.forEach((column, index) => {
            params.append(`columns[${index}][data]`, column);
            params.append(`columns[${index}][name]`, "");
            params.append(`columns[${index}][searchable]`, "true");
            params.append(`columns[${index}][orderable]`, "false");
            params.append(`columns[${index}][search][value]`, "");
            params.append(`columns[${index}][search][regex]`, "false");
          });
          body = params.toString();
          headers["Content-Type"] = "application/x-www-form-urlencoded";
        }
        const response = await axios.post(
          `${config.API_URL}/products/getactive_product`,
          body,
          { headers }
        );
        if (!active) {
          return;
        }
        let json = response.data || {};
        if (useEncrypt) {
          const dec = resolveEncryptedResponse(json);
          if (dec && typeof dec === "object") {
            json = dec;
          }
        }
        const data = Array.isArray(json.data) ? json.data : [];
        setRows(data);
        const filtered = Number(json.recordsFiltered);
        const total = Number.isFinite(filtered) ? filtered : Number(json.recordsTotal) || data.length;
        setTotalCount(total);
        setError("");
      } catch (err) {
        if (!active) {
          return;
        }
        const status = err.response?.status;
        const message = err.response?.data?.message || err.message || "Failed to load inventory.";
        setError(status ? `Error ${status}: ${message}` : message);
        setRows([]);
        setTotalCount(0);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayedRows = useMemo(() => rows, [rows]);

  const paginationSx = useMemo(
    () => ({
      "& .MuiTablePagination-toolbar": {
        flexWrap: "nowrap",
        alignItems: "baseline",
        minHeight: 20,
        px: 0
      },
      "& .MuiTablePagination-displayedRows": {
        marginLeft: 0
      }
    }),
    []
  );

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setPage(0);
  };

  const renderTextCell = value => {
    if (!value) {
      return "";
    }
    const stringValue = String(value);
    if (stringValue.length < 10) {
      return stringValue;
    }
    return (
      <Tooltip title={stringValue}>
        <span>{`${stringValue.substring(0, 35)}…`}</span>
      </Tooltip>
    );
  };

  return (
    <>
      <Box className="page-container" >
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-between">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography className="page-title">
                  Inventory Report
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      setPage(0);
                      setSearchTerm(searchInput.trim());
                    }
                  }}
                  placeholder="Search"
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleClearSearch}>
                            <Iconify icon="eva:close-fill" width={18} height={18} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    }
                  }}
                />
              </Grid2>

            </Grid2>
          </Box>
          {/* Table Section */}
          <TableContainer sx={{ width: "100%", overflowX: "auto", px: { xs: 1, sm: 0 } }}>
            <Table
              size="small"
              className="table-cell-border"
              sx={{
                minWidth: { xs: 1200, sm: 1200 },
                tableLayout: { xs: "auto", sm: "fixed" },
                borderSpacing: 0
              }}
            >
              <colgroup>
                {columnWidths.map((width, index) => (
                  <col key={`col-${index}`} style={{ width }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell
                    colSpan={5}
                    align="left"
                    sx={{
                      ...commonStyles.headerColspanCell,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      py: { xs: 1, sm: 1.5 }
                    }}
                  >
                    Product Details
                  </TableCell>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{
                      ...commonStyles.headerColspanCell,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      py: { xs: 1, sm: 1.5 }
                    }}
                  >
                    Box
                  </TableCell>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{
                      ...commonStyles.headerColspanCell,
                      borderRight: "none",
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      py: { xs: 1, sm: 1.5 }
                    }}
                  >
                    Piece
                  </TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: headerBgColor }}>
                  <TableCell sx={{ ...commonStyles.headerCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Code</TableCell>
                  <TableCell sx={{ ...commonStyles.headerCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Name</TableCell>
                  <TableCell sx={{ ...commonStyles.headerCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Category</TableCell>
                  <TableCell sx={{ ...commonStyles.headerCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Brand</TableCell>
                  <TableCell sx={{ ...commonStyles.headerCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Size</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Cost P</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Sale P</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Profit %</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Qty</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Cost P</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Sale P</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Profit %</TableCell>
                  <TableCell align="right" sx={{ ...commonStyles.headerNumericCell, borderRight: "none", fontSize: { xs: '0.7rem', sm: '0.75rem' }, py: { xs: 1, sm: 1.5 } }}>Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: rowsPerPage }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell sx={commonStyles.bodyCell}>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                      <TableCell sx={commonStyles.bodyCell}>
                        <Skeleton variant="text" width="90%" />
                      </TableCell>
                      <TableCell sx={commonStyles.bodyCell}>
                        <Skeleton variant="text" width="70%" />
                      </TableCell>
                      <TableCell sx={commonStyles.bodyCell}>
                        <Skeleton variant="text" width="60%" />
                      </TableCell>
                      <TableCell sx={commonStyles.bodyCell}>
                        <Skeleton variant="text" width="50%" />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="60%" sx={{ ml: "auto" }} />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="60%" sx={{ ml: "auto" }} />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="50%" sx={{ ml: "auto" }} />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="40%" sx={{ ml: "auto" }} />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="60%" sx={{ ml: "auto" }} />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="60%" sx={{ ml: "auto" }} />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="50%" sx={{ ml: "auto" }} />
                      </TableCell>
                      <TableCell align="right" sx={commonStyles.bodyNumericCell}>
                        <Skeleton variant="text" width="40%" sx={{ ml: "auto" }} />
                      </TableCell>
                    </TableRow>
                  ))
                  : displayedRows.length > 0
                    ? displayedRows.map((row, rowIndex) => (
                      <TableRow
                        key={row.id || row.code || rowIndex}
                        className="table-cell-style"
                       
                      >
                        <TableCell sx={commonStyles.bodyCell}>{renderTextCell(row.code)}</TableCell>
                        <TableCell sx={commonStyles.bodyCell}>{renderTextCell(row.name)}</TableCell>
                        <TableCell sx={commonStyles.bodyCell}>{renderTextCell(row.categories)}</TableCell>
                        <TableCell sx={commonStyles.bodyCell}>{renderTextCell(row.brand)}</TableCell>
                        <TableCell sx={commonStyles.bodyCell}>{renderTextCell(row.size)}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.box_cost ?? ""}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.price ?? ""}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.box_diff ?? ""}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.quantity ?? ""}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.piece_cost ?? ""}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.piece_price ?? ""}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.piece_diff ?? ""}</TableCell>
                        <TableCell align="right" sx={commonStyles.bodyNumericCell}>{row.split_quantity ?? ""}</TableCell>
                      </TableRow>
                    ))
                    : (
                      <TableRow>
                        <TableCell colSpan={columns.length} sx={{ py: 4 }}>
                          <Typography align="center" sx={{ color: "#637381", fontSize: "0.75rem" }}>
                            No records found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Section */}
          <Box sx={{ overflowX: 'auto' }} display="flex" justifyContent={{ xs: 'center', sm: 'flex-end' }} alignItems="center">
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage=""
              className="pagination-styles"
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Inventory_Report;
